/**
 * Password input with a show/hide toggle — styled to match the marketing-
 * shell input convention (Signup, InviteAccept, CompleteProfile all use this
 * instead of each hand-rolling their own). `visible` is owned by the PARENT,
 * not this component: when a form has both a password AND confirm-password
 * field, both instances take the SAME `visible` state so toggling one
 * reveals both in sync, rather than each field having its own independent
 * (and easy-to-forget-to-click-twice) toggle.
 */
import { Lock, Eye, EyeOff } from 'lucide-react';
import { useTheme, THEMES } from '@/pages/home/marketing-shell';

interface PasswordFieldProps {
  value: string;
  onChange: (value: string) => void;
  visible: boolean;
  /** Only render the eye-icon toggle button on one of the two fields when
   *  used as a password + confirm-password pair — both still respect
   *  `visible`, only one needs to control it. */
  showToggle?: boolean;
  onToggleVisible?: () => void;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
}

export default function PasswordField({
  value,
  onChange,
  visible,
  showToggle = true,
  onToggleVisible,
  placeholder,
  autoComplete = 'new-password',
  autoFocus,
}: PasswordFieldProps) {
  const { theme } = useTheme();
  const t = THEMES[theme];

  return (
    <div style={{ position: 'relative' }}>
      <Lock
        size={15}
        color={t.mutedFg}
        style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
      />
      <input
        style={{
          width: '100%',
          padding: `11px ${showToggle ? 40 : 14}px 11px 40px`,
          borderRadius: 10,
          background: t.muted,
          border: `1px solid ${t.border}`,
          color: t.fg,
          fontSize: 14,
          fontFamily: "'Inter', sans-serif",
          outline: 'none',
        }}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        onFocus={(e) => { e.currentTarget.style.borderColor = t.green; }}
        onBlur={(e) => { e.currentTarget.style.borderColor = t.border; }}
      />
      {showToggle && (
        <button
          type="button"
          onClick={onToggleVisible}
          aria-label={visible ? 'Hide password' : 'Show password'}
          style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {visible ? <EyeOff size={16} color={t.mutedFg} /> : <Eye size={16} color={t.mutedFg} />}
        </button>
      )}
    </div>
  );
}
